import {
  AdditionExprContext,
  DivisionExprContext,
  formulaLexer,
  formulaListener,
  IfFunctionTypeContext,
  LogicTermContext,
  MultiplicationExprContext,
  SubtractionExprContext,
  TwoExpressionsContext,
} from '@doss/common-all'
import { ParserRuleContext, Token } from 'antlr4'
import {
  calculateDistance,
  isDateFunctionContext,
  isNumericFunctionContext,
  isTextFunctionContext,
} from './utils'

/**
 * Listener that identifies the most relevant context for autocompletion based on the user's
 * cursor position.
 */
export class AutocompleteListener implements formulaListener {
  private relevantContext: ParserRuleContext | undefined
  private closestRelevantContext: ParserRuleContext | undefined
  private maxPosition = -1

  /**
   * Initializes the listener with the cursor position in the input text.
   */
  constructor(private readonly position: number) {}

  /**
   * Called when entering every rule context during the parse tree traversal.
   * Identifies the closest relevant context based on the cursor position.
   */
  enterEveryRule(ctx: ParserRuleContext) {
    const relevantToken = this.getRelevantToken(ctx)
    if (relevantToken) {
      const tokenStartPosition = relevantToken.start
      const tokenStopPosition = relevantToken.stop

      if (
        tokenStartPosition <= this.position &&
        tokenStopPosition < this.position &&
        tokenStartPosition > this.maxPosition
      ) {
        // The cursor is after the relevant token and it's closer than the previous closest token
        this.closestRelevantContext = ctx
        this.maxPosition = tokenStartPosition
      }
    }
  }

  /**
   * Called when exiting every rule context during the parse tree traversal. Updates the relevant
   * context to the deepest relevant context within the closest relevant context.
   */
  exitEveryRule(ctx: ParserRuleContext) {
    if (ctx === this.closestRelevantContext) {
      // Find the deepest relevant context within the closest relevant context
      const deepestContext = this.findDeepestRelevantContext(ctx)
      if (deepestContext) {
        this.relevantContext = deepestContext
      }
    }
  }

  visitTerminal() {
    // Empty implementation to satisfy the interface
  }

  visitErrorNode() {
    // Empty implementation to satisfy the interface
  }

  /**
   * Retrieves the relevant token from the given context based on the cursor position.
   */
  private getRelevantToken(ctx: ParserRuleContext): Token | undefined {
    let relevantToken: Token | undefined
    let maxPosition = -1

    // Get the token types of function types, operators, and logic conditions
    const relevantTokenTypes = [
      formulaLexer.PLUS,
      formulaLexer.MINUS,
      formulaLexer.MULTIPLY,
      formulaLexer.DIVIDE,
      formulaLexer.SUM,
      formulaLexer.MAX,
      formulaLexer.MIN,
      formulaLexer.COUNT,
      formulaLexer.COUNT_DISTINCT,
      formulaLexer.AVERAGE,
      formulaLexer.SUMPRODUCT,
      formulaLexer.SUMIF,
      formulaLexer.COUNTIF,
      formulaLexer.AVERAGEIF,
      formulaLexer.IFS,
      formulaLexer.CEIL,
      formulaLexer.FLOOR,
      formulaLexer.ROUND,
      formulaLexer.MAXIFS,
      formulaLexer.MINIFS,
      // Text functions
      formulaLexer.UPPER,
      formulaLexer.LOWER,
      formulaLexer.TRIM,
      formulaLexer.LEN,
      formulaLexer.CONCAT,
      formulaLexer.LEFT,
      formulaLexer.RIGHT,
      formulaLexer.MID,
      formulaLexer.SUBSTITUTE,
      // Date functions
      formulaLexer.DATE,
      formulaLexer.DATEADD,
      formulaLexer.DATEDIFF,
      formulaLexer.YEAR,
      formulaLexer.MONTH,
      formulaLexer.DAY,
      // Type conversion functions
      formulaLexer.TO_TEXT,
      formulaLexer.TO_NUMBER,
      formulaLexer.TO_DATE,
      formulaLexer.COLUMN_ID,
      formulaLexer.NUMBER,
      formulaLexer.STRING,
      formulaLexer.BOOLEAN,
      formulaLexer.COMMA,
      formulaLexer.GT,
      formulaLexer.GTE,
      formulaLexer.LT,
      formulaLexer.LTE,
      formulaLexer.EQ,
      formulaLexer.NOT_EQ,
      formulaLexer.CONTAINS,
      formulaLexer.DOES_NOT_CONTAIN,
      formulaLexer.CONTAINS_ANY,
      formulaLexer.CONTAINS_ALL,
      formulaLexer.DOES_NOT_CONTAIN_ANY,
      formulaLexer.IN,
      formulaLexer.NOT_IN,
    ]

    // Iterate over the relevant token types
    for (const tokenType of relevantTokenTypes) {
      // Get the tokens of the current token type within the current context
      const tokens = ctx.getTokens(tokenType)

      // Iterate over the tokens
      for (const token of tokens) {
        if (token.symbol.start <= this.position && token.symbol.start > maxPosition) {
          maxPosition = token.symbol.start
          relevantToken = token.symbol
        }
      }
    }

    return relevantToken
  }

  /**
   * Finds the deepest relevant context within the given context that is closest to the current
   * position.
   */
  private findDeepestRelevantContext(ctx: ParserRuleContext): ParserRuleContext | undefined {
    let closestContext: ParserRuleContext | undefined
    let closestDistance = Infinity

    // Check if the current context is a relevant context
    if (this.isRelevantContext(ctx)) {
      const distance = calculateDistance(this.position, ctx)
      if (distance < closestDistance) {
        closestContext = ctx
        closestDistance = distance
      }
    }

    for (const child of ctx.children ?? []) {
      if (child instanceof ParserRuleContext) {
        const deepestChild = this.findDeepestRelevantContext(child)
        if (deepestChild) {
          const distance = calculateDistance(this.position, deepestChild)
          if (distance < closestDistance) {
            closestContext = deepestChild
            closestDistance = distance
          }
        }
      }
    }

    // If no closest context found in children, check the parents
    if (!closestContext) {
      let parent = ctx.parentCtx
      while (parent) {
        if (this.isRelevantContext(parent)) {
          return parent
        }
        parent = parent.parentCtx
      }
    }

    return closestContext
  }

  /**
   * Checks if the given parser rule context is a relevant context for autocompletion. These
   * are the contexts that provide information about what formula operator is the primary one
   * for the current context
   */
  private isRelevantContext(ctx: ParserRuleContext): boolean {
    return (
      isNumericFunctionContext(ctx) ||
      isTextFunctionContext(ctx) ||
      isDateFunctionContext(ctx) ||
      ctx instanceof TwoExpressionsContext ||
      ctx instanceof IfFunctionTypeContext ||
      ctx instanceof AdditionExprContext ||
      ctx instanceof SubtractionExprContext ||
      ctx instanceof MultiplicationExprContext ||
      ctx instanceof DivisionExprContext ||
      ctx instanceof LogicTermContext
    )
  }

  /**
   * Retrieves the most relevant context for autocompletion.
   */
  getRelevantContext(): ParserRuleContext | undefined {
    return this.relevantContext
  }
}
